import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Clock, Users, Calendar, 
  DollarSign, CheckCircle, BookOpen, Star, MapPin, ShoppingCart
} from 'lucide-react';
import SEO from '../components/SEO';
import { programsAPI } from '../services/api';
import AddToCartModal from '../components/cart/AddToCartModal';
import {
  formatScheduleDate,
  formatScheduleTime,
  getProgramScheduleRange,
  getSessionScheduleEntries,
} from '../utils/programSchedules';

const ProgramDetailPage: React.FC = () => {
  const { slug } = useParams();
  const [showCartModal, setShowCartModal] = useState(false);

  // Fetch program details
  const { data: program, isLoading, error } = useQuery(
    ['program', slug],
    () => programsAPI.getProgram(slug || ''),
    { enabled: !!slug }
  );

  // Fetch sessions for this program
  const { data: sessions = [] } = useQuery(
    ['program-sessions', program?.data?.id],
    () => programsAPI.getSessions({ program: program?.data?.id }),
    { 
      enabled: !!program?.data?.id,
      select: (response) => response.data?.results || response.data || []
    }
  );

  if (isLoading) {
    return (
      <div className="pt-28 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !program?.data) {
    return (
      <div className="pt-28 min-h-screen">
        <section className="gradient-bg py-20">
          <div className="container-custom text-center text-white">
            <h1 className="text-4xl font-bold mb-4">Program Not Found</h1>
            <p className="text-white/80 mb-8">The program you're looking for doesn't exist.</p>
            <Link to="/programs" className="btn-white">
              <ArrowLeft size={18} className="mr-2" />
              Back to Programs
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const programData = program.data;
  const isOneTimeProgram = programData.payment_type === 'one_time' || programData.is_one_time_payment;
  const displayPrice = programData.effective_price || programData.one_time_price || programData.monthly_price || programData.price;
  const activeSessions = sessions.filter((s: any) => s.is_active && !s.is_full);
  const programScheduleRange = getProgramScheduleRange({
    ...programData,
    available_sessions: activeSessions,
  });

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": programData.name,
    "description": programData.description,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Future Minds Academy",
      "sameAs": "https://futuremindsacademy.net"
    },
    "educationalLevel": programData.level || "Beginner to Advanced",
    "audience": {
      "@type": "EducationalAudience",
      "educationalRole": "student",
      "audienceType": `Children ages ${programData.min_age || 5}-${programData.max_age || 14}`
    },
    "offers": {
      "@type": "Offer",
      "price": displayPrice || "150",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="pt-28">
      <SEO 
        title={`${programData.name} | STEM Program for Kids`}
        description={programData.description || `Learn ${programData.name} at Future Minds Academy. Hands-on STEM education for children in Clayton, NC.`}
        keywords={`${programData.name}, STEM program, kids ${programData.track_name || 'robotics'} class, children coding Clayton NC`}
        canonical={`/programs/${slug}`}
        structuredData={courseSchema}
      />
      {/* Hero Section */}
      <section className="gradient-bg py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/programs" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft size={20} className="mr-2" />
              Back to Programs
            </Link>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                {programData.track_name && (
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-4">
                    {programData.track_name}
                  </span>
                )}
                <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
                  {programData.name}
                </h1>
                <p className="text-xl text-white/80 mb-8 leading-relaxed">
                  {programData.description}
                </p>
                
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="flex items-center">
                    <Users size={20} className="mr-2 text-secondary-400" />
                    <span>Ages {programData.min_age}-{programData.max_age}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={20} className="mr-2 text-secondary-400" />
                    <span>{programData.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <Star size={20} className="mr-2 text-secondary-400" />
                    <span className="capitalize">{programData.level || 'All Levels'}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <Link 
                    to={`/registration?program=${programData.id}`} 
                    className="btn-secondary text-lg px-8 py-4"
                  >
                    Enroll Now
                    <ArrowRight className="ml-2" size={20} />
                  </Link>
                  <button
                    onClick={() => setShowCartModal(true)}
                    className="btn-white text-lg px-6 py-4 flex items-center"
                  >
                    <ShoppingCart className="mr-2" size={20} />
                    Add to Cart
                  </button>
                  <div className="text-3xl font-bold">
                    ${displayPrice}{isOneTimeProgram ? '' : '/mo'}
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                {programData.image ? (
                  <img 
                    src={programData.image} 
                    alt={programData.name}
                    className="rounded-2xl shadow-2xl"
                  />
                ) : (
                  <div className="bg-white/10 rounded-2xl p-12 text-center">
                    <BookOpen size={120} className="mx-auto text-white/40 mb-4" />
                    <p className="text-white/60">Program Image</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Details */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* What You'll Learn */}
              {programData.curriculum && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="text-3xl font-bold mb-6">What You'll Learn</h2>
                  <div className="prose max-w-none text-gray-600">
                    {programData.curriculum}
                  </div>
                </motion.div>
              )}

              {/* Key Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                <h2 className="text-3xl font-bold mb-6">Program Highlights</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Hands-on project-based learning',
                    'Small class sizes for personalized attention',
                    'Industry-standard tools and technologies',
                    'Take-home projects and materials',
                    'Certificate of completion',
                    'Expert instructors with real-world experience'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle size={20} className="text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Prerequisites */}
              {programData.prerequisites && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="text-3xl font-bold mb-6">Prerequisites</h2>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <p className="text-gray-600">{programData.prerequisites}</p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Quick Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="card p-6 mb-6 sticky top-24"
              >
                <h3 className="text-xl font-bold mb-6">Program Details</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-gray-600 flex items-center">
                      <DollarSign size={18} className="mr-2" />
                      Price
                    </span>
                    <span className="font-bold text-lg">${displayPrice} {isOneTimeProgram ? 'one time' : '/mo'}</span>
                  </div>
                  <div className="py-3 border-b">
                    <span className="text-gray-600 flex items-center mb-1">
                      <Calendar size={18} className="mr-2" />
                      Schedule
                    </span>
                    <p className="font-semibold">
                      {isOneTimeProgram
                        ? (programScheduleRange || 'Schedule announced after registration')
                        : `${programData.sessions_per_month || activeSessions.length || 4} sessions per month`}
                    </p>
                  </div>
                  <div className="py-3 border-b">
                    <span className="text-gray-600 flex items-center mb-1">
                      <DollarSign size={18} className="mr-2" />
                      Payment
                    </span>
                    <p className="font-semibold">
                      {isOneTimeProgram ? 'One-time payment. No monthly subscription charge.' : 'Monthly subscription billing.'}
                    </p>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-gray-600 flex items-center">
                      <Users size={18} className="mr-2" />
                      Age Range
                    </span>
                    <span className="font-semibold">{programData.min_age}-{programData.max_age} years</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-gray-600 flex items-center">
                      <Clock size={18} className="mr-2" />
                      Duration
                    </span>
                    <span className="font-semibold">{programData.duration}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-gray-600 flex items-center">
                      <Star size={18} className="mr-2" />
                      Level
                    </span>
                    <span className="font-semibold capitalize">{programData.level || 'All Levels'}</span>
                  </div>
                </div>

                <Link 
                  to={`/registration?program=${programData.id}`}
                  className="btn-primary w-full justify-center"
                >
                  Enroll Now
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Sessions */}
      {activeSessions.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-4">Available Sessions</h2>
              <p className="text-gray-600">Choose a session that fits your schedule</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeSessions.map((session: any, index: number) => {
                const scheduleEntries = getSessionScheduleEntries(session);

                return (
                  <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card p-6"
                >
                  <h3 className="text-lg font-bold mb-4">{session.name}</h3>
                  
                  <div className="space-y-3 mb-6 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      {session.days_display || session.day_of_week || 'Saturdays'} • {session.start_time} - {session.end_time}
                    </div>
                    {scheduleEntries.length === 0 && session.start_date && (
                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2" />
                        {formatScheduleDate(session.start_date)}{session.end_date ? ` to ${formatScheduleDate(session.end_date)}` : ''}
                      </div>
                    )}
                    {scheduleEntries.length > 0 && (
                      <div className="rounded-lg bg-gray-50 p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Scheduled Class Dates
                        </p>
                        <div className="space-y-2">
                          {scheduleEntries.map((entry) => (
                            <div key={entry.key} className="text-gray-600">
                              <div className="font-medium text-gray-700">{formatScheduleDate(entry.date)}</div>
                              <div className="text-xs">
                                {formatScheduleTime(entry.startTime)} - {formatScheduleTime(entry.endTime)}
                                {entry.location ? ` - ${entry.location}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <MapPin size={16} className="mr-2" />
                      {session.location || 'Location TBD'}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Users size={16} className="mr-2" />
                      {session.available_spots} spots available
                    </div>
                  </div>

                  <Link 
                    to={`/registration?session=${session.id}`}
                    className="btn-primary w-full justify-center text-sm"
                  >
                    {isOneTimeProgram ? 'Enroll in This Schedule' : 'Select This Session'}
                  </Link>
                </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section gradient-bg">
        <div className="container-custom text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Learning?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join {programData.name} and give your child the skills they need for the future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/registration?program=${programData.id}`} className="btn-secondary text-lg px-8 py-4">
                Enroll Now
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-white text-lg px-8 py-4">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        program={{
          id: programData.id,
          name: programData.name,
          price: programData.price,
          monthly_price: programData.monthly_price,
          one_time_price: programData.one_time_price,
          effective_price: programData.effective_price,
          payment_type: programData.payment_type,
          is_one_time_payment: programData.is_one_time_payment,
          image: programData.image,
        }}
      />
    </div>
  );
};

export default ProgramDetailPage;
